// Authentication Service - Login, Register, User Management
import api from './api';

// Role-based default routes (uses primary role or first matched from roles array)
export const getRoleBasedRoute = (role, roles) => {
  const routes = {
    superadmin: '/superadmin/dashboard',
    tenantadmin: '/tenantadmin/admin-panel',
    siteadmin: '/admin/dashboard',
    admin: '/admin/dashboard',
    doctor: '/doctor/doctor-hub',
    surgeon: '/doctor/doctor-hub',
    anesthetist: '/doctor/dashboard',
    dentist: '/doctor/doctor-hub',
    physiotherapist: '/doctor/dashboard',
    nurse: '/nurse/dashboard',
    headnurse: '/nurse/dashboard',
    pharmacist: '/staff/pharmacy',
    labtechnician: '/staff/laboratory',
    radiologist: '/staff/radiology',
    receptionist: '/staff/dashboard',
    billingstaff: '/staff/billing',
    medicalrecords: '/staff/documents',
    staff: '/staff/dashboard',
    hrmanager: '/admin/admin-panel',
    itadmin: '/admin/admin-panel',
    qualityofficer: '/admin/dashboard',
    patient: '/patient/dashboard',
  };
  const priority = ['superadmin', 'tenantadmin', 'doctor', 'surgeon', 'dentist', 'anesthetist', 'physiotherapist', 'nurse', 'headnurse', 'siteadmin', 'admin', 'itadmin', 'hrmanager', 'qualityofficer', 'pharmacist', 'labtechnician', 'radiologist', 'receptionist', 'billingstaff', 'medicalrecords', 'staff', 'patient'];
  const userRoles = roles || (role ? [role] : []);
  for (const r of priority) {
    if (userRoles.includes(r)) return routes[r] || '/';
  }
  return routes[role] || '/';
};

// Role display names
export const getRoleDisplayName = (role) => {
  const names = {
    superadmin: 'Super Admin',
    tenantadmin: 'Tenant Admin',
    siteadmin: 'Site Admin',
    admin: 'Administrator',
    doctor: 'Doctor',
    surgeon: 'Surgeon',
    anesthetist: 'Anesthetist',
    nurse: 'Nurse',
    headnurse: 'Head Nurse',
    pharmacist: 'Pharmacist',
    labtechnician: 'Lab Technician',
    radiologist: 'Radiologist',
    physiotherapist: 'Physiotherapist',
    dentist: 'Dentist',
    receptionist: 'Receptionist',
    billingstaff: 'Billing Staff',
    medicalrecords: 'Medical Records',
    staff: 'Staff',
    hrmanager: 'HR Manager',
    itadmin: 'IT Administrator',
    qualityofficer: 'Quality Officer',
    patient: 'Patient',
  };
  return names[role] || role;
};

// Role permissions
export const ROLE_PERMISSIONS = {
  superadmin: ['all'],

  // ── System / Admin Roles ──
  tenantadmin: [
    'admin.manage_users', 'admin.manage_roles', 'admin.manage_settings',
    'admin.view_audit_logs', 'admin.manage_departments', 'admin.manage_billing',
    'reports.view', 'reports.export',
  ],
  siteadmin: [
    'admin.manage_users', 'admin.manage_settings', 'admin.view_audit_logs',
    'reports.view',
  ],
  admin: [
    'admin.manage_users', 'admin.manage_settings', 'admin.view_audit_logs',
    'reports.view',
  ],

  // ── Clinical Roles ──
  doctor: [
    'patients.view', 'patients.edit', 'patients.create',
    'appointments.view', 'appointments.create', 'appointments.edit',
    'clinical.view', 'clinical.create', 'clinical.edit',
    'prescriptions.create', 'prescriptions.view',
    'lab.order', 'lab.view_results',
    'imaging.order', 'imaging.view_results',
    'documents.view', 'documents.upload',
    'reports.view',
  ],
  surgeon: [
    'patients.view', 'patients.edit',
    'appointments.view',
    'clinical.view', 'clinical.create', 'clinical.edit',
    'surgery.view', 'surgery.create', 'surgery.edit',
    'prescriptions.create',
    'lab.order', 'lab.view_results',
    'imaging.order', 'imaging.view_results',
    'documents.view', 'documents.upload',
  ],
  anesthetist: [
    'patients.view',
    'clinical.view', 'clinical.create',
    'surgery.view',
    'prescriptions.create',
    'lab.view_results',
    'documents.view',
  ],
  nurse: [
    'patients.view', 'patients.edit',
    'appointments.view',
    'clinical.view', 'clinical.create',
    'vitals.create', 'vitals.view',
    'nursing.view', 'nursing.create', 'nursing.edit',
    'prescriptions.view',
    'lab.view_results',
    'documents.view', 'documents.upload',
  ],
  headnurse: [
    'patients.view', 'patients.edit',
    'appointments.view',
    'clinical.view', 'clinical.create',
    'vitals.create', 'vitals.view',
    'nursing.view', 'nursing.create', 'nursing.edit', 'nursing.approve',
    'prescriptions.view',
    'lab.view_results',
    'documents.view', 'documents.upload',
    'reports.view',
  ],
  pharmacist: [
    'patients.view',
    'prescriptions.view',
    'pharmacy.view', 'pharmacy.dispense', 'pharmacy.manage_inventory',
    'documents.view',
  ],
  labtechnician: [
    'patients.view',
    'lab.view_results', 'lab.create_results', 'lab.edit_results',
    'documents.view', 'documents.upload',
  ],
  radiologist: [
    'patients.view',
    'imaging.view_results', 'imaging.create_reports', 'imaging.edit_reports',
    'documents.view', 'documents.upload',
  ],
  physiotherapist: [
    'patients.view', 'patients.edit',
    'appointments.view', 'appointments.create',
    'clinical.view', 'clinical.create',
    'documents.view', 'documents.upload',
  ],
  dentist: [
    'patients.view', 'patients.edit', 'patients.create',
    'appointments.view', 'appointments.create',
    'clinical.view', 'clinical.create',
    'prescriptions.create',
    'documents.view', 'documents.upload',
  ],

  // ── Operational / Front-Desk Roles ──
  receptionist: [
    'patients.view', 'patients.create', 'patients.edit',
    'appointments.view', 'appointments.create', 'appointments.edit',
    'documents.view',
  ],
  billingstaff: [
    'patients.view',
    'billing.view', 'billing.create', 'billing.edit',
    'insurance.view', 'insurance.create',
    'reports.view',
  ],
  medicalrecords: [
    'patients.view',
    'documents.view', 'documents.upload', 'documents.manage',
    'reports.view',
  ],
  staff: [
    'patients.view', 'patients.create', 'patients.edit',
    'appointments.view', 'appointments.create', 'appointments.edit',
    'documents.view',
  ],

  // ── Administrative Roles ──
  hrmanager: [
    'admin.manage_users', 'admin.view_audit_logs',
    'reports.view',
  ],
  itadmin: [
    'admin.manage_settings', 'admin.view_audit_logs', 'admin.manage_users',
    'reports.view', 'reports.export',
  ],
  qualityofficer: [
    'admin.view_audit_logs',
    'reports.view', 'reports.export',
    'patients.view',
    'documents.view',
  ],

  // ── Patient ──
  patient: [
    'portal.view', 'portal.appointments', 'portal.records', 'portal.prescriptions',
  ],
};

export const authService = {
  // SSO Login — sends Firebase ID token to backend, gets EMR JWT back
  // Returns { success, user, redirectTo } for existing users,
  // or { success: false, newUser: true, redirectTo } for new users (→ trial signup).
  async ssoLogin(firebaseIdToken, provider, profile) {
    try {
      const response = await api.post('/api/v1/auth/sso', {
        id_token: firebaseIdToken,
        provider,
        name: profile?.name || '',
        avatar: profile?.avatar || '',
      });

      const data = response.data;

      // Backend returns { new_user: true, redirect_to } for unregistered SSO users
      if (data.new_user) {
        return {
          success: false,
          newUser: true,
          redirectTo: data.redirect_to,
          email: data.email,
          name: data.name,
        };
      }

      const { access_token, user } = data;

      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('emr_user', JSON.stringify(user));
      if (user.tenant_id) localStorage.setItem('emr_tenant_id', user.tenant_id);
      if (user.site_id) localStorage.setItem('emr_site_id', user.site_id);

      return {
        success: true,
        user,
        token: access_token,
        redirectTo: getRoleBasedRoute(user.role, user.roles),
      };
    } catch (error) {
      const message = error.response?.data?.detail || 'SSO login failed';
      return { success: false, error: message };
    }
  },

  // Login user
  async login(email, password) {
    try {
      const response = await api.post('/api/v1/auth/login', { email, password });
      const { access_token, user } = response.data;
      
      // Store token and user
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('emr_user', JSON.stringify(user));
      
      // Store tenant context for multi-tenant features
      if (user.tenant_id) localStorage.setItem('emr_tenant_id', user.tenant_id);
      if (user.site_id) localStorage.setItem('emr_site_id', user.site_id);
      
      return {
        success: true,
        user,
        token: access_token,
        redirectTo: getRoleBasedRoute(user.role, user.roles),
      };
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid email or password';
      return { success: false, error: message };
    }
  },

  // Register new user
  async register(userData) {
    try {
      const response = await api.post('/api/v1/auth/register', userData);
      const { access_token, user } = response.data;
      
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('emr_user', JSON.stringify(user));
      
      if (user.tenant_id) localStorage.setItem('emr_tenant_id', user.tenant_id);
      if (user.site_id) localStorage.setItem('emr_site_id', user.site_id);
      
      return {
        success: true,
        user,
        token: access_token,
        redirectTo: getRoleBasedRoute(user.role, user.roles),
      };
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      return { success: false, error: message };
    }
  },

  // Get current user info
  async getCurrentUser() {
    try {
      const response = await api.get('/api/v1/auth/me');
      return response.data;
    } catch (error) {
      return null;
    }
  },

  // Update current user's profile
  async updateProfile(data) {
    try {
      const response = await api.put('/api/v1/auth/profile', data);
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update profile';
      return { success: false, error: message };
    }
  },

  // Upload avatar image
  async uploadAvatar(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/api/v1/auth/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to upload avatar';
      return { success: false, error: message };
    }
  },

  // Logout
  async logout() {
    try {
      await api.post('/api/v1/auth/logout');
    } catch (error) {
      // Ignore errors on logout
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('emr_user');
      localStorage.removeItem('emr_tenant_id');
      localStorage.removeItem('emr_site_id');
    }
  },

  // Check if user is authenticated
  isAuthenticated() {
    return !!localStorage.getItem('auth_token');
  },

  // Get stored user
  getStoredUser() {
    const user = localStorage.getItem('emr_user');
    return user ? JSON.parse(user) : null;
  },

  // Check permission (supports multi-role)
  hasPermission(permission, role, roles) {
    const userRoles = roles || (role ? [role] : []);
    for (const r of userRoles) {
      const perms = ROLE_PERMISSIONS[r] || [];
      if (perms.includes('all') || perms.includes(permission)) return true;
    }
    return false;
  },
};

export default authService;
